import { CareOSLoader } from '@/components/ui/careos-loader';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ImageCropModal } from '@/components/ui/image-crop-modal';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Camera, Loader2, User, Save, ShieldCheck, ChevronRight, ShieldAlert, Building2, FileText, ExternalLink } from 'lucide-react';
import { useHasRole } from '@/hooks/useHasRole';
import { compressImage } from '@/lib/image-utils';
import { useMyCompanies } from '@/hooks/useMyCompanies';
import { MedicalDisclaimerModal } from '@/components/legal/MedicalDisclaimerModal';

export default function Profile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isDPO = useHasRole('security_officer');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { companies } = useMyCompanies();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  const [cropModalOpen, setCropModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { data: profile, isLoading, isFetched } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      return data;
    },
    enabled: !!user?.id,
    staleTime: 0,
  });

  useEffect(() => {
    if (isFetched) {
      setFullName(profile?.full_name || '');
      setPhone(profile?.phone || '');
    }
  }, [profile, isFetched]);

  const updateProfileMutation = useMutation({
    mutationFn: async (data: { full_name: string; phone: string }) => {
      if (!user?.id) throw new Error(t('common.error'));

      const { error } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: user.id,
            full_name: data.full_name,
            phone: data.phone,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      toast.success(t('profile.profileUpdated'));
      navigate('/app');
    },
    onError: (error: any) => {
      console.error('Error updating profile:', error);
      toast.error(error?.message || t('profile.errorUpdatingProfile'));
    },
  });

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('profile.selectImage'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // Limit relaxed because we will compress
      toast.error(t('profile.imageMaxSize'));
      return;
    }

    setSelectedFile(file);
    setCropModalOpen(true);

    event.target.value = '';
  };

  const handleCroppedImage = async (croppedBlob: Blob) => {
    if (!user?.id) return;

    setCropModalOpen(false);
    setSelectedFile(null);
    setIsUploading(true);

    try {
      // Compress the cropped image even further
      const { blob: finalBlob } = await compressImage(croppedBlob, {
        maxWidth: 512,
        maxHeight: 512,
        quality: 0.8
      });

      const fileName = `${user.id}/avatar.jpg`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, finalBlob, { upsert: true, contentType: 'image/jpeg' });

      if (uploadError) throw uploadError;

      // Store the file path reference - signed URLs will be generated when viewing
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      const { error: updateError } = await supabase
        .from('profiles')
        .upsert(
          {
            user_id: user.id,
            avatar_url: `${urlData.publicUrl}?t=${Date.now()}`,
            updated_at: new Date().toISOString(),
          },
          { onConflict: 'user_id' }
        );

      if (updateError) throw updateError;

      queryClient.invalidateQueries({ queryKey: ['profile', user?.id] });
      toast.success(t('profile.photoUpdated'));
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error(t('profile.errorUploadingPhoto'));
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateProfileMutation.mutate({ full_name: fullName, phone });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (isLoading) {
    return (
      <AppLayout title={t('profile.title')} showBack showFAB={false}>
        <div className="flex items-center justify-center h-64">
          <CareOSLoader size="lg" label={t('common.loading')} />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={t('profile.title')} showBack onBack={() => navigate('/app')} showFAB={false}>
      <div className="p-4 space-y-6">
        {/* Avatar Section */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <SignedAvatar
              src={profile?.avatar_url}
              bucket="avatars"
              className="w-24 h-24 border-4 border-primary/20"
              alt={fullName}
              fallback={
                <span className="bg-primary/10 text-primary text-xl w-full h-full flex items-center justify-center rounded-full">
                  {fullName ? getInitials(fullName) : <User className="w-8 h-8" />}
                </span>
              }
            />
            <Button
              size="icon"
              variant="secondary"
              className="absolute -bottom-2 -right-2 rounded-full w-10 h-10 shadow-md"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              {isUploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Camera className="w-4 h-4" />
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileSelect}
            />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('profile.clickToChangePhoto')}
          </p>
        </div>

        {/* Image Crop Modal */}
        <ImageCropModal
          open={cropModalOpen}
          imageFile={selectedFile}
          onClose={() => {
            setCropModalOpen(false);
            setSelectedFile(null);
          }}
          onCrop={handleCroppedImage}
          size={256}
        />

        {/* Profile Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('profile.personalInfo')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">{t('profile.email')}</Label>
                <Input
                  id="email"
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="bg-muted"
                />
                <p className="text-xs text-muted-foreground">
                  {t('profile.emailCantChange')}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="fullName">{t('profile.fullName')}</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder={t('profile.fullNamePlaceholder')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t('profile.phone')}</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('profile.phonePlaceholder')}
                />
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={updateProfileMutation.isPending}
              >
                {updateProfileMutation.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {t('profile.saveChanges')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* My companies */}
        {companies.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                {t('profile.myCompanies')}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {companies.map((c) => {
                const roleLabel =
                  c.role === 'manager'
                    ? t('roles.manager')
                    : c.role === 'coordinator'
                      ? t('company.roleCoordinator')
                      : t('roles.caregiver');
                const canOpen = c.role === 'manager' || c.role === 'coordinator';
                return (
                  <button
                    key={c.companyId}
                    type="button"
                    disabled={!canOpen}
                    onClick={() => canOpen && navigate('/app/company')}
                    className={`w-full flex items-center gap-3 p-3 rounded-lg border bg-card text-left transition-colors ${canOpen ? 'hover:bg-muted/50 cursor-pointer' : 'cursor-default'}`}
                  >
                    <SignedAvatar
                      src={c.logoUrl}
                      bucket="company-logos"
                      className="w-10 h-10"
                      fallback={
                        <span className="bg-primary/10 text-primary flex items-center justify-center w-full h-full">
                          <Building2 className="w-5 h-5" />
                        </span>
                      }
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{c.companyName}</p>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary">
                        {roleLabel}
                      </span>
                    </div>
                    {canOpen && <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </button>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Privacy Center link */}
        <Card>
          <button
            type="button"
            onClick={() => navigate('/app/privacy')}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-3">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <div>
                <p className="font-medium">Centro de privacidade</p>
                <p className="text-xs text-muted-foreground">
                  Gerencie consentimentos, exporte ou exclua seus dados (LGPD).
                </p>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground" />
          </button>
        </Card>

        {/* Legal notices and policies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('profile.legalSection')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              type="button"
              onClick={() => setDisclaimerOpen(true)}
              className="w-full flex items-center justify-between p-3 text-left rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <ShieldAlert className="w-5 h-5 text-info" />
                <p className="font-medium">{t('profile.medicalDisclaimerLink')}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </button>

            <a
              href="https://careos.life/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 text-left rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <p className="font-medium">{t('profile.privacyPolicyLink')}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>

            <a
              href="https://careos.life/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="w-full flex items-center justify-between p-3 text-left rounded-lg border bg-card hover:bg-muted/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-primary" />
                <p className="font-medium">{t('profile.termsLink')}</p>
              </div>
              <ExternalLink className="w-4 h-4 text-muted-foreground" />
            </a>
          </CardContent>
        </Card>

        {disclaimerOpen && (
          <MedicalDisclaimerModal
            open={disclaimerOpen}
            onClose={() => setDisclaimerOpen(false)}
          />
        )}
      </div>
    </AppLayout>
  );
}